import * as React from "react";
import { getTotalSelectedUdfCount, type UDFCategory } from "../ReportServices";
import { SectionHeader, type TemplateTabViewStyles } from "./TemplateTabViewStyles";

type TemplateUdfSectionProps = {
  styles: TemplateTabViewStyles;
  isConfigurationLocked: boolean;
  udfCategories: UDFCategory[];
  selectedUdfs: string;
  includeUdfs: boolean;
  setIncludeUdfs: (value: boolean) => void;
  handleUdfCategoryToggle: (categoryName: string, isSelected: boolean) => void;
  handleUdfItemToggle: (itemId: string, isSelected: boolean) => void;
  handleMasterUdfToggle: (isSelected: boolean) => void;
  setHasGeneratedWorkbook: (value: boolean) => void;
};

const TemplateUdfSection: React.FC<TemplateUdfSectionProps> = ({
  styles,
  isConfigurationLocked,
  udfCategories,
  selectedUdfs,
  includeUdfs,
  setIncludeUdfs,
  handleUdfCategoryToggle,
  handleUdfItemToggle,
  handleMasterUdfToggle,
  setHasGeneratedWorkbook,
}) => {
  return (
    <div className={styles.section}>
      <SectionHeader styles={styles} num={4} title="Preselected user defined fields (optional)" />
      <div style={{ fontSize: "11px", color: "#6D6E71", padding: "6px 12px 8px 12px", borderBottom: "0.25pt solid #E8F0F0" }}>
        Select whether to support custom fields and which ones to preselect in the analyzer.
      </div>
      <div className={styles.sectionBody}>
        <div style={{ marginBottom: "10px", paddingBottom: "10px", borderBottom: "0.25pt solid #E0E0E0" }}>
          <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontWeight: 600, color: "#001C71" }}>
            <input
              type="checkbox"
              checked={includeUdfs}
              onChange={(event) => { setIncludeUdfs(event.target.checked); setHasGeneratedWorkbook(false); }}
              disabled={isConfigurationLocked}
              style={{ marginRight: "8px", cursor: "pointer", accentColor: "#001C71" }}
            />
            Include user defined fields
          </label>
        </div>
        <div style={{ opacity: includeUdfs ? 1 : 0.4, pointerEvents: includeUdfs ? "auto" : "none" }}>
          {udfCategories.length === 0 ? (
            <div className={styles.emptyRow}>No custom fields available.</div>
          ) : (
            <>
              {(() => {
                const { selected, total } = getTotalSelectedUdfCount(selectedUdfs, udfCategories);
                const allSelected = selected === total && total > 0;
                const someSelected = selected > 0 && selected < total;
                return (
                  <div style={{ marginBottom: "12px", paddingBottom: "12px", borderBottom: "0.25pt solid #E0E0E0" }}>
                    <label style={{ display: "flex", alignItems: "center", cursor: "pointer", color: "#001C71" }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(input) => {
                          if (input) input.indeterminate = someSelected;
                        }}
                        onChange={(event) => handleMasterUdfToggle(event.target.checked)}
                        disabled={isConfigurationLocked}
                        style={{ marginRight: "8px", cursor: "pointer" }}
                      />
                      <span style={{ fontWeight: 500 }}>Select All</span>
                      <span style={{ marginLeft: "auto", color: "#666", fontSize: "0.85em" }}>
                        {selected}/{total}
                      </span>
                    </label>
                  </div>
                );
              })()}
              {udfCategories.map((category) => {
                const selectedIds = new Set(selectedUdfs ? selectedUdfs.split(", ") : []);
                const categoryItemsSelected = category.items.filter((item) => selectedIds.has(item.id)).length;
                const allCategoryItemsSelected = categoryItemsSelected === category.items.length;
                const someSelected = categoryItemsSelected > 0 && categoryItemsSelected < category.items.length;
                return (
                  <div key={category.displayName} style={{ marginBottom: "12px" }}>
                    <label style={{ display: "flex", alignItems: "center", cursor: "pointer", marginBottom: "4px", fontWeight: 500, color: "#001C71" }}>
                      <input
                        type="checkbox"
                        checked={allCategoryItemsSelected}
                        ref={(input) => {
                          if (input) input.indeterminate = someSelected;
                        }}
                        onChange={(event) => handleUdfCategoryToggle(category.displayName, event.target.checked)}
                        disabled={isConfigurationLocked}
                        style={{ marginRight: "8px", cursor: "pointer" }}
                      />
                      <span>{category.displayName}</span>
                      <span style={{ marginLeft: "auto", color: "#666", fontSize: "0.85em" }}>
                        {categoryItemsSelected}/{category.items.length}
                      </span>
                    </label>
                    <div style={{ marginLeft: "24px", marginTop: "4px" }}>
                      {category.items.map((item) => {
                        const isSelected = selectedIds.has(item.id);
                        return (
                          <label key={item.id} style={{ display: "flex", alignItems: "center", cursor: "pointer", marginBottom: "4px", fontSize: "0.95em" }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(event) => handleUdfItemToggle(item.id, event.target.checked)}
                              disabled={isConfigurationLocked}
                              style={{ marginRight: "8px", cursor: "pointer" }}
                            />
                            <span>{item.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateUdfSection;
